import html2pdf from 'html2pdf.js';
import { EVITALS_LOGO_BASE64 } from '../../../../assets/evitalsLogoBase64';
import { getPath } from './planState';

function asText(value) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }
  if (Array.isArray(value)) return value.map(asText).filter(Boolean).join(', ');
  if (typeof value === 'object') {
    const named = value.insuranceName
      || value.insurance_name
      || value.name
      || value.label
      || value.title
      || '';
    return typeof named === 'object' ? '' : String(named).trim();
  }
  return '';
}

function escapeHtml(value) {
  return asText(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatDate(val) {
  if (!val) return '—';
  const str = String(val).trim();
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[2]}/${match[3]}/${match[1]}`;
  return str;
}

function nodeShown(node, plan, ctx) {
  if (!node) return false;
  if (typeof node.showWhen === 'function' && !node.showWhen(plan, ctx)) return false;
  return true;
}

function pillsHtml(items) {
  const list = (items || []).map(asText).filter(Boolean);
  if (!list.length) return '<p class="muted">None selected</p>';
  return `<div class="picks">${list.map((item) => `<span class="pick">${escapeHtml(item)}</span>`).join('<span class="pick-gap"> · </span>')}</div>`;
}

function fieldText(node, plan) {
  if (node.input === 'multiselect' && node.namePath) {
    const named = asText(getPath(plan, node.namePath));
    if (named) return named;
  }
  const raw = getPath(plan, node.path);
  if (node.input === 'date') {
    const formatted = formatDate(raw);
    return formatted === '—' ? '' : formatted;
  }
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
  return asText(raw);
}

function renderPlanNodes(nodes, plan, ctx) {
  return (nodes || [])
    .filter((node) => nodeShown(node, plan, ctx))
    .map((node) => renderPlanNode(node, plan, ctx))
    .join('');
}

function renderPlanNode(node, plan, ctx) {
  if (node.type === 'card') {
    const title = node.title ? `<h3 class="card-title">${escapeHtml(node.title)}</h3>` : '';
    return `<div class="inner-card">${title}${renderPlanNodes(node.children, plan, ctx)}</div>`;
  }
  if (node.type === 'heading') return `<div class="subhead">${escapeHtml(node.text || '')}</div>`;
  if (node.type === 'grid') {
    const cols = Math.max(1, Number(node.cols) || 2);
    const cells = (node.children || [])
      .filter((child) => nodeShown(child, plan, ctx))
      .map((child) => `<td class="grid-cell">${renderPlanNode(child, plan, ctx)}</td>`);
    if (!cells.length) return '';
    const rows = [];
    for (let i = 0; i < cells.length; i += cols) {
      const slice = cells.slice(i, i + cols);
      while (slice.length < cols) slice.push('<td class="grid-cell"></td>');
      rows.push(`<tr>${slice.join('')}</tr>`);
    }
    return `<table class="field-grid cols-${cols}"><tbody>${rows.join('')}</tbody></table>`;
  }
  if (node.type === 'field') {
    return `<div class="line-field"><span class="fact-label">${escapeHtml(node.label)}</span><div class="fact-value">${escapeHtml(fieldText(node, plan) || '—')}</div></div>`;
  }
  if (node.type === 'chips') {
    const selected = getPath(plan, node.path);
    return `<div class="line-field"><span class="fact-label">${escapeHtml(node.label || 'Selected')}</span>${pillsHtml(Array.isArray(selected) ? selected : [])}</div>`;
  }
  if (node.type === 'accordion') {
    const groups = (node.groups || []).map((group) => {
      const selected = getPath(plan, group.path || node.path);
      const all = Array.isArray(selected) ? selected : [];
      const picked = (group.items || []).filter((item) => all.includes(item));
      if (!picked.length) return '';
      return `<div class="line-field"><span class="fact-label">${escapeHtml(group.label || 'Selected')}</span>${pillsHtml(picked)}</div>`;
    }).join('');
    return groups || '<p class="muted">None selected</p>';
  }
  if (node.type === 'pills') {
    const current = asText(getPath(plan, node.path));
    return `<div class="line-field">${pillsHtml(current ? [current] : [])}</div>`;
  }
  if (node.type === 'score') {
    const selected = getPath(plan, node.path);
    const ids = Array.isArray(selected) ? selected : [];
    const picked = (node.items || []).filter((row) => ids.includes(row.id));
    const total = picked.reduce((sum, row) => sum + (Number(row.pts) || 0), 0);
    return `<div class="line-field"><span class="fact-label">${escapeHtml(node.totalLabel || 'Score')} · ${total}</span>${pillsHtml(picked.map((row) => row.label))}</div>`;
  }
  if (node.type === 'rows') {
    const rows = getPath(plan, node.path) || [];
    const cols = node.columns || [];
    if (!rows.length) return '<p class="muted">None recorded</p>';
    return `<table class="data-table"><thead><tr>${cols.map((column) => `<th>${escapeHtml(column.label)}</th>`).join('')}</tr></thead><tbody>${
      rows.map((row) => `<tr>${cols.map((column) => {
        const raw = row[column.key];
        const text = column.input === 'date' ? formatDate(raw) : (raw || '—');
        return `<td>${escapeHtml(text)}</td>`;
      }).join('')}</tr>`).join('')
    }</tbody></table>`;
  }
  if (node.type === 'meds') {
    const rows = getPath(plan, node.path) || [];
    if (!rows.length) return '<p class="muted">No medications added.</p>';
    return `<table class="data-table"><thead><tr><th>Medication</th><th>Dose</th><th>Frequency</th><th>Indication / notes</th></tr></thead><tbody>${
      rows.map((row) => `<tr><td><b>${escapeHtml(row.name || '—')}</b></td><td>${escapeHtml(row.dose || '—')}</td><td>${escapeHtml(row.freq || row.frequency || '—')}</td><td>${escapeHtml(row.note || row.purpose || '—')}</td></tr>`).join('')
    }</tbody></table>`;
  }
  if (node.type === 'monitors') {
    const current = getPath(plan, node.path) || {};
    const rows = (node.items || []).filter((item) => current[item.id]);
    if (!rows.length) return '<p class="muted">None selected</p>';
    return `<table class="data-table"><thead><tr><th>Parameter</th><th>Method</th><th>Frequency</th></tr></thead><tbody>${
      rows.map((item) => `<tr><td>${escapeHtml(item.label)}</td><td>${escapeHtml(item.method || '—')}</td><td>${escapeHtml(current[item.id])}</td></tr>`).join('')
    }</tbody></table>`;
  }
  if (node.type === 'attests') {
    const checked = (node.items || []).filter((item) => Boolean(getPath(plan, item.path)));
    return pillsHtml(checked.map((item) => item.label));
  }
  if (node.type === 'readout') return `<p class="readout">${escapeHtml(node.text || '')}</p>`;
  if (node.type === 'empty') return `<p class="muted">${escapeHtml([node.title, node.text].filter(Boolean).join(' '))}</p>`;
  return '';
}

function renderSchemaSections(sections, plan, ctx) {
  return sections.map((section) => `
    <div class="card sec">
      <div class="sec-title"><span class="sec-n">${escapeHtml(section.n || '')}</span><span class="sec-label">${escapeHtml(section.label || '')}</span></div>
      ${renderPlanNodes(section.cards, plan, ctx)}
    </div>
  `).join('');
}

const PRINT_STYLES = `
  @page {
    size: letter portrait;
    margin: 0.45in;
  }
  * {
    box-sizing: border-box;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .dcp-print-body {
    font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #0f172a;
    background: #f8fafc;
    margin: 0;
    padding: 16px;
    line-height: 1.45;
    font-size: 13px;
    width: 100%;
    max-width: 100%;
  }
  .sheet { display: block; }
  .card {
    display: block;
    background: #ffffff;
    border: 0;
    border-radius: 0;
    padding: 14px 16px 8px;
    margin: 0 0 16px;
    page-break-inside: auto;
    break-inside: auto;
  }
  .inner-card { margin-top: 12px; page-break-inside: auto; break-inside: auto; }
  .field-grid { width: 100%; border: 0; border-collapse: collapse; margin: 0; }
  .field-grid td.grid-cell {
    border: 0;
    background: transparent;
    vertical-align: top;
    padding: 0 18px 10px 0;
  }
  .field-grid.cols-2 td.grid-cell { width: 50%; }
  .field-grid.cols-3 td.grid-cell { width: 33.33%; }
  .field-grid.cols-4 td.grid-cell { width: 25%; }
  .card-title {
    margin: 0 0 6px;
    font-size: 13px;
    color: #1B2A4A;
    font-weight: 700;
    page-break-after: avoid;
    break-after: avoid;
  }
  .line-field {
    margin: 0 0 10px;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .picks {
    margin: 8px 0 2px;
    font-size: 12px;
    line-height: 1.7;
    color: #1e3a8a;
    font-weight: 600;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .pick { color: #1e3a8a; font-weight: 600; }
  .pick-gap { color: #94a3b8; font-weight: 400; }
  .readout { margin: 0 0 8px; font-size: 12px; color: #334155; }
  .subhead {
    margin: 8px 0 4px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #64748b;
  }
  .brand {
    width: 100%;
    border-collapse: collapse;
    margin: 0 0 14px;
    border: 0;
    border-bottom: 1px solid #f1f5f9;
    border-radius: 0;
    font-size: 13px;
  }
  .brand td {
    vertical-align: top;
    border: 0;
    background: transparent;
    padding: 0 0 14px;
  }
  .brand-left { width: 42%; }
  .brand-logo-img {
    height: 36px;
    width: auto;
    max-width: 150px;
    object-fit: contain;
    display: block;
  }
  .brand-sub {
    display: block;
    margin-top: 6px;
    font-size: 11px;
    letter-spacing: .08em;
    color: #64748b;
    font-weight: 700;
    text-transform: uppercase;
  }
  .doc { width: 58%; text-align: right; }
  .doc-status {
    display: block;
    margin-top: 6px;
    font-size: 13px;
    line-height: 18px;
    font-weight: 700;
    text-align: right;
  }
  .doc-status.ap { color: #15803d; }
  .doc-status.pd { color: #c2410c; }
  .doc-status.df { color: #475569; }
  .doc-title {
    display: block;
    margin: 0;
    font-size: 18px;
    line-height: 1.35;
    color: #03045e;
    font-weight: 700;
  }
  .st {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    line-height: 18px;
    font-weight: 700;
    text-align: center;
    white-space: nowrap;
  }
  .st.ap { background: #ecfdf5; color: #15803d; }
  .st.pd { background: #fff7ed; color: #c2410c; }
  .st.df { background: #f1f5f9; color: #475569; }
  .fact-table {
    width: 100%;
    border: 0;
    border-collapse: collapse;
    margin: 0;
    font-size: 13px;
  }
  .fact-table td.fact {
    width: 50%;
    border: 0;
    background: transparent;
    padding: 0 18px 12px 0;
    vertical-align: top;
  }
  .fact-label {
    display: block;
    margin: 0 0 8px;
    font-size: 11px;
    line-height: 14px;
    font-weight: 600;
    color: #64748b;
  }
  .fact-value {
    display: block;
    font-size: 13px;
    line-height: 18px;
    font-weight: 600;
    color: #0f172a;
  }
  .meta {
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid #f1f5f9;
    font-size: 12px;
    color: #475569;
  }
  .meta b { color: #03045e; font-weight: 700; }
  .sec-title {
    display: block;
    margin: 0 0 12px;
    text-align: left;
    font-size: 14px;
    line-height: 22px;
    font-weight: 700;
    color: #1e293b;
  }
  .sec-n {
    display: inline;
    width: auto;
    margin: 0 8px 0 0;
    padding: 0;
    background: transparent;
    color: #03045e;
    font-size: 14px;
    line-height: 22px;
    font-weight: 700;
    border-radius: 0;
  }
  .sec-label {
    display: inline;
    padding: 0;
    font-size: 14px;
    line-height: 22px;
    font-weight: 700;
    color: #1e293b;
  }
  table.data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    margin-top: 4px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
  }
  table.data-table th {
    padding: 8px 10px;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    color: #334155;
    font-size: 11px;
    font-weight: 700;
    background: #f8fafc;
  }
  table.data-table td {
    padding: 8px 10px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: top;
    color: #334155;
    background: #fff;
  }
  table.data-table tr:last-child td { border-bottom: 0; }
  .muted { margin: 0; color: #64748b; font-size: 12px; }
  .emerg {
    border: 1px solid #fecaca;
    background: #fef2f2;
    border-radius: 10px;
    padding: 14px 16px;
  }
  .emerg h3 { margin: 0 0 6px; color: #dc2626; font-size: 14px; font-weight: 700; }
  .emerg p { margin: 0 0 8px; font-size: 12px; color: #7f1d1d; }
  .emerg .thresholds { font-size: 12px; color: #0f172a; }
  .sign-table { width: 100%; border: 0; border-collapse: collapse; margin: 0; }
  .sign-table td {
    width: 33%;
    border: 0;
    background: transparent;
    vertical-align: top;
    padding: 0 12px 0 0;
  }
  .sign .line {
    border-top: 1px solid #cbd5e1;
    padding-top: 8px;
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
  }
  .sign .name {
    margin-top: 4px;
    font-size: 13px;
    color: #0f172a;
    font-weight: 600;
  }
`;

function buildContentBody(plan = {}, patient = {}, options = {}) {
  const {
    program = 'PCM',
    diseaseLabel = 'Care Plan',
    status = 'pending',
  } = options;

  const patientName = plan.patient?.name
    || patient.name
    || [patient.first_name, patient.last_name].filter(Boolean).join(' ')
    || 'Patient';
  const dob = formatDate(plan.patient?.dob || patient.dob);
  const sex = plan.patient?.sex || patient.gender || patient.sex || '—';
  const phone = plan.patient?.phone || patient.phone || '—';
  const mrn = plan.patient?.mrn || patient.mrn || patient.id || '—';
  const startDate = formatDate(plan.patient?.startDate);
  const insurance = asText(plan.patient?.insurancePrimary)
    || asText(plan.patient?.insurance)
    || asText(patient.insurance_name)
    || asText(patient.primary_insurance)
    || asText(patient.insurance)
    || '—';
  const emergencyName = plan.patient?.emergencyContact
    || [plan.patient?.emergencyFirstName, plan.patient?.emergencyLastName].filter(Boolean).join(' ')
    || '—';
  const emergencyRel = plan.patient?.emergencyRelation ? ` (${plan.patient.emergencyRelation})` : '';
  const emergencyPhone = plan.patient?.emergencyPhone ? ` · ${plan.patient.emergencyPhone}` : '';

  const providerName = plan.signoff?.providerName || plan.team?.pcp || '—';
  const managerName = plan.signoff?.managerName || plan.team?.careManager || '—';
  const reviewInterval = plan.signoff?.reviewInterval || 'Annually';
  const nextReview = formatDate(plan.signoff?.nextReview);

  const diagnosisPrimary = plan.diagnosis?.primary || '—';
  const diagnosisDate = formatDate(plan.diagnosis?.date);
  const confirmedBy = Array.isArray(plan.diagnosis?.confirmedBy) && plan.diagnosis.confirmedBy.length
    ? plan.diagnosis.confirmedBy.join(', ')
    : '—';
  const selectedConditions = Array.isArray(plan.diagnosis?.selected) && plan.diagnosis.selected.length
    ? plan.diagnosis.selected.join(', ')
    : '';

  const baselineHr = plan.baseline?.hr ? `${plan.baseline.hr} bpm` : '—';
  const baselineBp = (plan.baseline?.systolic && plan.baseline?.diastolic)
    ? `${plan.baseline.systolic}/${plan.baseline.diastolic} mmHg`
    : '—';
  const baselineRhythm = plan.baseline?.rhythm || '—';
  const baselineEcgDate = formatDate(plan.baseline?.ecgDate);
  const baselineQtc = plan.baseline?.qtc ? `${plan.baseline.qtc} ms` : '—';
  const baselineLvef = plan.baseline?.lvef ? `${plan.baseline.lvef}%` : '—';

  const chadsItems = Array.isArray(plan.risk?.chadsvasc) ? plan.risk.chadsvasc : [];
  const hasbledItems = Array.isArray(plan.risk?.hasbled) ? plan.risk.hasbled : [];
  const nyha = plan.risk?.nyha || '—';

  const hasDevice = plan.device?.type && plan.device.type !== 'None';
  const deviceType = plan.device?.type || 'None';
  const deviceMfr = plan.device?.manufacturer || '—';
  const deviceModel = plan.device?.model || '—';
  const deviceLastCheck = formatDate(plan.device?.lastCheck);

  const goals = Array.isArray(plan.goals) ? plan.goals : [];
  const medList = Array.isArray(plan.meds?.list) ? plan.meds.list : [];
  const anticoag = plan.meds?.anticoag || '—';
  const inrGoal = plan.meds?.inrGoal || '—';
  const rateCtrl = Array.isArray(plan.meds?.rate) && plan.meds.rate.length ? plan.meds.rate.join(', ') : '—';
  const rhythmCtrl = Array.isArray(plan.meds?.rhythm) && plan.meds.rhythm.length ? plan.meds.rhythm.join(', ') : '—';

  const lowHr = plan.emergency?.lowHr ? `< ${plan.emergency.lowHr} bpm` : '—';
  const highHr = plan.emergency?.highHr ? `> ${plan.emergency.highHr} bpm` : '—';
  const emergencyActions = plan.emergency?.actions || 'Contact care team or proceed to nearest emergency facility.';

  const educationTopics = Array.isArray(plan.education?.topics) && plan.education.topics.length
    ? plan.education.topics.join(', ')
    : 'Disease self-management & adherence';
  const lifestyle = Array.isArray(plan.education?.lifestyle) && plan.education.lifestyle.length
    ? plan.education.lifestyle.join(', ')
    : 'Cardiovascular wellness & healthy nutrition';

  const diseaseDef = options.disease;
  const planSections = typeof diseaseDef?.sections === 'function' ? diseaseDef.sections(program) : null;
  const planCtx = typeof diseaseDef?.derive === 'function' ? (diseaseDef.derive(plan) || {}) : {};

  const isApproved = status === 'active';
  const statusLabel = isApproved ? 'Approved' : status === 'pending' ? 'Pending approval' : 'Draft';
  const statusClass = isApproved ? 'ap' : status === 'pending' ? 'pd' : 'df';
  const programSub = program === 'PCM' ? 'PRINCIPAL CARE MANAGEMENT' : 'CHRONIC CARE MANAGEMENT';

  const factCell = (label, value) => `
    <td class="fact">
      <div class="fact-label">${escapeHtml(label)}</div>
      <div class="fact-value">${escapeHtml(value || '—')}</div>
    </td>`;
  const factTable = (cells) => {
    const rows = [];
    for (let i = 0; i < cells.length; i += 2) {
      rows.push(`<tr>${cells[i]}${cells[i + 1] || '<td class="fact"></td>'}</tr>`);
    }
    return `<table class="fact-table"><tbody>${rows.join('')}</tbody></table>`;
  };

  let sectionNo = 0;
  const sectionTitle = (title) => {
    sectionNo += 1;
    const n = String(sectionNo).padStart(2, '0');
    return `<div class="sec-title"><span class="sec-n">${n}</span><span class="sec-label">${escapeHtml(title)}</span></div>`;
  };
  const showRisk = Boolean(chadsItems.length || hasbledItems.length || hasDevice || nyha !== '—');

  const brandCard = `
    <div class="card sec">
      <table class="brand">
        <tr>
          <td class="brand-left">
            <img class="brand-logo-img" src="${EVITALS_LOGO_BASE64}" alt="e-Vitals" />
            <span class="brand-sub">${escapeHtml(programSub)}</span>
          </td>
          <td class="doc">
            <div class="doc-title">${escapeHtml(diagnosisPrimary || diseaseLabel)}</div>
            <div class="doc-status ${statusClass}">${escapeHtml(statusLabel)}</div>
          </td>
        </tr>
      </table>
    </div>`;

  if (planSections?.length) {
    return `<div class="sheet">${brandCard}${renderSchemaSections(planSections, plan, planCtx)}</div>`;
  }

  return `
    <div class="sheet">
    <div class="card sec">
      <table class="brand">
        <tr>
          <td class="brand-left">
            <img class="brand-logo-img" src="${EVITALS_LOGO_BASE64}" alt="e-Vitals" />
            <span class="brand-sub">${escapeHtml(programSub)}</span>
          </td>
          <td class="doc">
            <div class="doc-title">${escapeHtml(diagnosisPrimary || diseaseLabel)}</div>
            <div class="doc-status ${statusClass}">${escapeHtml(statusLabel)}</div>
          </td>
        </tr>
      </table>
      ${sectionTitle('Patient & care team')}
      ${factTable([
        factCell('Patient', patientName),
        factCell('Patient ID / MRN', mrn),
        factCell('Date of birth', dob),
        factCell('Sex', sex),
        factCell('Phone', phone),
        factCell('Insurance', insurance),
        factCell('Provider', providerName),
        factCell('Care manager', managerName),
        factCell('Emergency contact', `${emergencyName}${emergencyRel}${emergencyPhone}`),
        factCell('Care plan start', startDate),
      ])}
      <div class="meta">
        Created <b>${escapeHtml(startDate)}</b> by <b>${escapeHtml(managerName)}</b>
        &nbsp;·&nbsp; Approved by <b>${escapeHtml(providerName)}</b>
        &nbsp;·&nbsp; Next review <b>${escapeHtml(nextReview)}</b> (${escapeHtml(reviewInterval)})
      </div>
    </div>

    <div class="card sec">
      ${sectionTitle('Diagnosis & clinical baseline')}
      ${factTable([
        factCell('Primary condition', diagnosisPrimary),
        factCell('Diagnosis date', diagnosisDate),
        factCell('Confirmed by', confirmedBy),
        factCell('Subtypes / categories', selectedConditions || '—'),
        factCell('Resting heart rate', baselineHr),
        factCell('Blood pressure', baselineBp),
        factCell('Baseline rhythm', baselineRhythm),
        factCell('ECG date', baselineEcgDate),
        factCell('QTc interval', baselineQtc),
        factCell('LVEF (%)', baselineLvef),
      ])}
    </div>

    ${showRisk ? `
      <div class="card sec">
        ${sectionTitle('Clinical risk & device')}
        ${factTable([
          ...(chadsItems.length ? [factCell('CHA2DS2-VASc', chadsItems.join(', '))] : []),
          ...(hasbledItems.length ? [factCell('HAS-BLED', hasbledItems.join(', '))] : []),
          factCell('NYHA class', nyha),
          factCell('Cardiac device', hasDevice ? `${deviceType} (${deviceMfr} ${deviceModel}) · Last check: ${deviceLastCheck}` : 'None'),
        ])}
      </div>
    ` : ''}

    <div class="card sec">
      ${sectionTitle('Care goals')}
      ${goals.length ? `
        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 50%;">Goal</th>
              <th style="width: 30%;">Target / Metric</th>
              <th style="width: 20%;">Target Date</th>
            </tr>
          </thead>
          <tbody>
            ${goals.map((g) => `
              <tr>
                <td><b>${escapeHtml(g.goal || '—')}</b></td>
                <td>${escapeHtml(g.target || '—')}</td>
                <td>${formatDate(g.date)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      ` : '<p class="muted">Individualized clinical goals active in EHR.</p>'}
    </div>

    <div class="card sec">
      ${sectionTitle('Medication management')}
      ${factTable([
        factCell('Anticoagulation', anticoag),
        factCell('Target INR', inrGoal),
        factCell('Rate control', rateCtrl),
        factCell('Rhythm control', rhythmCtrl),
      ])}
      ${medList.length ? `
        <table class="data-table">
          <thead>
            <tr>
              <th>Medication</th>
              <th>Dosage</th>
              <th>Frequency</th>
              <th>Route</th>
              <th>Purpose / Indication</th>
            </tr>
          </thead>
          <tbody>
            ${medList.map((m) => `
              <tr>
                <td><b>${escapeHtml(m.name || '—')}</b></td>
                <td>${escapeHtml(m.dose || '—')}</td>
                <td>${escapeHtml(m.frequency || '—')}</td>
                <td>${escapeHtml(m.route || 'Oral')}</td>
                <td>${escapeHtml(m.purpose || '—')}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      ` : '<p class="muted">Current medication list reconciled in clinical profile.</p>'}
    </div>

    <div class="card sec">
      ${sectionTitle('Emergency plan')}
    <div class="emerg">
      <h3>Call 911 right away</h3>
      <p>Chest pain, fainting or blacking out, severe shortness of breath, sudden facial droop or weakness, or repeated device shocks.</p>
      <div class="thresholds">
        <b>Thresholds:</b> Resting HR ${escapeHtml(lowHr)} or ${escapeHtml(highHr)}
        &nbsp;·&nbsp; <b>Action steps:</b> ${escapeHtml(emergencyActions)}
      </div>
    </div>
    </div>

    <div class="card sec">
      ${sectionTitle('Education & lifestyle')}
      ${factTable([
        factCell('Topics covered', educationTopics),
        factCell('Lifestyle recommendations', lifestyle),
        factCell('Teach-back confirmed', plan.education?.teachBack ? 'Yes' : 'Confirmed with patient'),
        factCell('Materials provided', plan.education?.materials ? 'Yes' : 'Provided'),
      ])}
    </div>

    <div class="card sec">
    ${sectionTitle('Review & sign-off')}
    <table class="sign-table"><tbody><tr>
      <td>
        <div class="line">Patient signature · Date</div>
        <div class="name">${escapeHtml(patientName)}</div>
      </td>
      <td>
        <div class="line">Care Manager signature · Date</div>
        <div class="name">${escapeHtml(managerName)}</div>
      </td>
      <td>
        <div class="line">Provider signature · Date</div>
        <div class="name">${escapeHtml(providerName)}</div>
      </td>
    </tr></tbody></table>
    </div>
    </div>
  `;
}

export function buildDiseaseCarePlanPrintHtml(plan = {}, patient = {}, options = {}) {
  const bodyHtml = buildContentBody(plan, patient, options);
  const title = plan.diagnosis?.primary || options.diseaseLabel || 'Care Plan';
  const patientName = plan.patient?.name || patient.name || 'Patient';

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(title)} — ${escapeHtml(patientName)}</title>
  <style>${PRINT_STYLES}</style>
</head>
<body>
  <div class="dcp-print-body">
    ${bodyHtml}
  </div>
</body>
</html>`;
}

export function printDiseaseCarePlan(plan, patient, options = {}) {
  const html = buildDiseaseCarePlanPrintHtml(plan, patient, options);
  const w = window.open('', '_blank', 'width=880,height=1000');
  if (!w) {
    return false;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => {
    try {
      w.print();
    } catch {
      // ignore
    }
  }, 300);
  return true;
}

export async function downloadDiseaseCarePlanPdf(plan, patient, options = {}) {
  const patientName = plan.patient?.name
    || patient.name
    || [patient.first_name, patient.last_name].filter(Boolean).join(' ')
    || 'Patient';
  const cleanName = patientName.replace(/[^a-zA-Z0-9_-]/g, '_');
  const filename = `${cleanName}_${(options.diseaseLabel || 'Care_Plan').replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`;

  const bodyHtml = buildContentBody(plan, patient, options);

  // Create temporary container directly in the DOM
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.top = '0';
  container.style.left = '-10000px';
  container.style.width = '7.7in';
  container.style.zIndex = '1';
  container.style.opacity = '1';
  container.style.pointerEvents = 'none';
  container.style.background = '#ffffff';
  container.style.overflow = 'visible';

  const styleEl = document.createElement('style');
  styleEl.textContent = PRINT_STYLES;

  const contentEl = document.createElement('div');
  contentEl.className = 'dcp-print-body';
  contentEl.innerHTML = bodyHtml;

  container.appendChild(styleEl);
  container.appendChild(contentEl);
  document.body.appendChild(container);

  try {
    const opt = {
      margin: [0.4, 0.4, 0.4, 0.4],
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 740,
        scrollX: 0,
        scrollY: 0,
      },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
      pagebreak: {
        mode: ['css', 'legacy'],
        avoid: ['.line-field', '.picks', '.data-table', '.sec-title', '.fact-table', '.card-title'],
      },
    };
    await html2pdf().set(opt).from(contentEl).save();
  } finally {
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }
}
